import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { locations, Funnel } from 'services/routes/locations';
import { useFunnel } from 'services/hooks/funnel';
import useSubscription from 'services/hooks/subscription/useSubscription';
import useAuthStore from 'services/hooks/auth/useAuthStore';

const useRedirect = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getFunnel } = useFunnel();
  const { isSubscriptionActive } = useSubscription();
  const { authStore, addRedirectParam, resetRedirectParam } = useAuthStore();
  const { isLoggedIn, redirect } = authStore;

  const defaultPathname = useMemo(() => {
    const subscriptionFunnel = getFunnel(Funnel.Subscription);
    const aiMusicGeneratorFunnel = getFunnel(Funnel.AIMusicGenerator);

    if (subscriptionFunnel) {
      return subscriptionFunnel.startedFrom;
    }
    if (aiMusicGeneratorFunnel) {
      return aiMusicGeneratorFunnel.startedFrom;
    }

    return locations.general.songList.pathname;
  }, [getFunnel]);

  const getRedirectionPath = useCallback((): string => {
    if (redirect) {
      if (!redirect.actionRequired) {
        resetRedirectParam();
      }
      return `${redirect.to}${redirect.hash || ''}`;
    }

    return defaultPathname;
  }, [defaultPathname, redirect, resetRedirectParam]);

  const redirectToRegistrationFlowOr = (
    action: () => void,
    params?: Record<string, unknown>
  ) => {
    if (!isLoggedIn) {
      const { pathname, search, hash } = location;
      addRedirectParam(`${pathname}${search}`, true, hash, params);
      navigate(locations.general.register.pathname(''));
    } else if (!isSubscriptionActive) {
      navigate(locations.subscription.pricing.pathname(''));
    } else {
      action();
    }
  };

  return {
    getRedirectionPath,
    redirectToRegistrationFlowOr,
  };
};

export default useRedirect;
